/* eslint-disable @typescript-eslint/no-explicit-any */
import { Fragment, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "src/components";
import SmartForm from "src/container/smart-form.component";
import { counterActions } from "src/context/counter-slice";
import { stockistActions } from "src/context/stockist-slice";
import { RootState } from "src/context/store";
import { useToast } from "src/hooks/useToast";
import { getClusterFromCompany } from "src/services/company-api";
import { getPaymentTerms } from "src/services/stockist-api";
import { getDistrictDetails } from "src/services/region-api";

interface IStockistCluster {
  companyData: any;
}

// Define the StockistCluster component
const StockistCluster = ({ companyData }: IStockistCluster) => {
  // Get the addEditFormData from the redux store
  const { toast } = useToast();
  const dispatch = useDispatch();

  const { sections } = useSelector((state: RootState) => state.employee);
  const { addEditFormData, mainFormEditMode, selectedStockistData } = useSelector((state: RootState) => state.stockist);
  const { clusterList } = useSelector((state: RootState) => state.counter);
  const defaultDistrict = selectedStockistData?.city?.district_code;
  const [districtList, setDistrictList] = useState([]);
  const [districtsCovered, setDistrictsCovered] = useState<any[]>([[]]);
  const [originalClusterList, setOriginalClusterList] = useState([]);
  const [companyToCompare, setCompanyToCompare] = useState("");
  // Define the state for the divisionList

  const resetClusterFields = () => {
    if (companyToCompare === addEditFormData?.company?.name) return;
    const updatedFormData = { ...addEditFormData };

    FORM_FIELDS.forEach((section: any[]) => {
      section.forEach((field: any) => {
        const fieldKey = field?.keyofForm;
        updatedFormData[fieldKey] = null; // Reset the field to null
      });
    });
    const updatedDistrictsCovered = districtsCovered.map(() => []);
    setDistrictsCovered(updatedDistrictsCovered);

    dispatch(stockistActions.setStockistLinkFormFullObject(updatedFormData));
  };

  const handleSelectCompany = async (data: any) => {
    if (!data?.code) return;
    getClusterFromCompany(data?.code)
      .then((res: any) => {
        if (res?.status === 200) {
          setOriginalClusterList(res.data);
          dispatch(counterActions.setClusterList(res.data));
          updateFormFields(res.data, "clusterCode");
          let shouldReset = false;

          Object.keys(addEditFormData)?.forEach((key: any) => {
            if (key.toString().startsWith("clusterCode")) {
              const isSelectedClusterMatched = res?.data.some((cluster: any) => cluster.cluster_code === addEditFormData[key]?.cluster_code);

              if (!isSelectedClusterMatched) {
                shouldReset = true;
              }
            }
          });
          if (shouldReset) {
            resetClusterFields();
          }
        }
      })
      .catch((err: any) => {
        toast({ description: JSON.stringify(err.message) });
      });
    // setstockistSearch((prevState) => ({ ...prevState, company: data?.counter_cmpny_location_id || [] }));
  };

  const appendClusterSections = () => {
    const clusterwiseSelectedDistrictArr: any[][] = [];

    const activeClusterLnk =
      addEditFormData?.stockistClusterLnk?.filter((item: any) => item?.active_status !== "N") || []; //M10-14028

    // Exit early if in non-edit mode and no clusters exist
    if (!mainFormEditMode && !activeClusterLnk.length) return;

    Object.keys(addEditFormData)?.forEach((key: any) => {
      if (key.startsWith("districtsCovered")) {
        // Create an array of districts for each cluster
        const eachDist = addEditFormData[key] || [];

        // Push districts for the current cluster to editDistrictArr
        clusterwiseSelectedDistrictArr.push(eachDist || []);
      }
    });

    const updatedFormFields = activeClusterLnk.map((_: any, index: number) => { //M10-14028
      // Update form fields for the current cluster
      return initialformFields.map((item) => {
        const currentItem = { ...item };
        const currentFormKey = currentItem.keyofForm;
        const slicedFormKey = currentFormKey?.split("__")[0];
        const newFormKey = `${slicedFormKey}__${index}`;
        return { ...currentItem, keyofForm: newFormKey };
      });
    });

    // Update state for districtsCovered and FORM_FIELDS
    setDistrictsCovered(clusterwiseSelectedDistrictArr);
    setFORM_FIELDS(updatedFormFields);
  };

  const getInitialApiResponses = async (stockistClusterLink: any[]) => {
    if (!mainFormEditMode && !stockistClusterLink?.length) return;

    for (const [index, obj] of stockistClusterLink.entries()) {
      if (obj?.Cluster_Code?.cluster_code) {
        try {
          const marginApiRes = await getPaymentTerms(`?cluster_code=${obj.Cluster_Code.cluster_code}&margin=&getCluster=true`);
          updateFormFields(marginApiRes?.data?.data || [], "margin", index);
        } catch (error) {
          console.error(`Error fetching margin API response for index ${index}:`, error);
        }
      }

      if (obj?.Cluster_Code?.cluster_code && obj?.paymentTermHdr?.margin) {
        try {
          const paymentTermApiRes = await getPaymentTerms(`?cluster_code=${obj.Cluster_Code.cluster_code}&margin=${obj.paymentTermHdr.margin}`);
          updateFormFields(paymentTermApiRes?.data?.data || [], "paymentTermHdr", index);
        } catch (error) {
          console.error(`Error fetching payment term API response for index ${index}:`, error);
        }
      }
    }
  };

  const getpayTerms = async (clusterCode: number, margin?: number | null) => {
    try {
      //M10-12174
      const isMarginMissing = margin === null || margin === undefined;
      const res = await getPaymentTerms(`?cluster_code=${clusterCode}&margin=${isMarginMissing ? "" : margin}${isMarginMissing ? "&getCluster=true" : ""}`);
      //M10-12174
      if (res?.status === 200) {
        const paymentTermsRes = res?.data?.data || [];
        return paymentTermsRes;
      } else {
        return [];
      }
    } catch (err) {
      console.error(err);
      toast({
        description: "Error fetching payment terms",
        variant: "destructive",
      });
      return [];
    }
  };

  const fetchDistrictList = () => {
    getDistrictDetails()
      .then((res) => {
        setDistrictList(res.data.data || []);
        updateFormFields(res.data.data || [], "districtsCovered");
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    const activeClusterLnk =
      addEditFormData?.stockistClusterLnk?.filter((item: any) => item?.active_status !== "N") || []; //M10-14028
    if (mainFormEditMode && activeClusterLnk.length > 0) { //M10-14028
      appendClusterSections();
      getInitialApiResponses(activeClusterLnk); //M10-14028
    }
    if (!mainFormEditMode) {
      if (defaultDistrict) {
        handleDistrictChange(0, [defaultDistrict]);

        dispatch(
          stockistActions.setFormData({
            value: [defaultDistrict],
            keyofForm: "districtsCovered__0",
          })
        );
      }
    }
    //M10-11464
    if (activeClusterLnk.length === 0) { //M10-14028
      handleRemoveDivision(0)
    }
    //M10-11464
  }, []);

  useEffect(() => {
    setCompanyToCompare(companyData?.name);
    fetchDistrictList();
    if (!companyData || (companyData && Object.keys(companyData)?.length === 0)) {
      handleSelectCompany(addEditFormData?.company);
    } else {
      handleSelectCompany(companyData);
    }
  }, [companyData?.code, addEditFormData?.company]);

  const initialformFields = [
    {
      label: "Cluster",
      type: "autocomplete",
      id: "1",
      suggestionList: clusterList,
      uniqueKey: "cluster_code",
      keyofForm: "clusterCode",
      displayKey: "cluster_name",
    },
    {
      id: "2",
      label: "Margin",
      type: "autocomplete",
      keyofForm: "margin",
      suggestionList: [],
      uniqueKey: "margin",
      displayKey: "margin",
      disabled: false,
    },
    {
      id: "3",
      label: "Payment Term Code",
      type: "autocomplete",
      keyofForm: "paymentTermHdr",
      suggestionList: [],
      uniqueKey: "id",
      displayKey: "paymentTermCode",
      disabled: false,
    },
    {
      id: "4",
      label: "Distribution channel",
      type: "input",
      keyofForm: "distributionChannel",
      disabled: false,
    },
    {
      label: "Districts To Be Covered",
      type: "multiselect",
      suggestionList: [],
      id: 17,
      keyofForm: "districtsCovered",
      displayKey: "district_name",
      uniqueKey: "district_code",
    },
  ];

  // Define the state for the form fields
  const [FORM_FIELDS, setFORM_FIELDS] = useState(
    Array.from({ length: sections === 0 ? 1 : sections }, (_, index) => [
      ...initialformFields.map((item) => {
        const currentItem = item;
        const currentFormKey = currentItem.keyofForm;
        const slicedFormKey = currentFormKey?.split("__")[0];
        const newFormKey = slicedFormKey + "__" + index;
        return { ...item, keyofForm: newFormKey };
      }),
    ])
  );

  // Define the function to handle adding a new division
  // const handleAddDivision = async () => {
  //   let updatedClusters: any = structuredClone(originalClusterList);

  //   Object.keys(addEditFormData)?.forEach((key: any) => {
  //     if (key.toString().startsWith("clusterCode")) {
  //       updatedClusters = updatedClusters.filter((cluster: any) => cluster.cluster_code !== addEditFormData[key]?.cluster_code);
  //     }
  //   });
  //   const newInitialFormFields = initialformFields.map((item) => {
  //     const currentItem = item;
  //     const currentFormKey = currentItem.keyofForm;
  //     const slicedFormKey = currentFormKey?.split("__")[0];
  //     const newFormKey = slicedFormKey + "__" + FORM_FIELDS.length;

  //     return {
  //       ...item,
  //       keyofForm: newFormKey,
  //       suggestionList: currentFormKey.includes("cluster")
  //         ? [...updatedClusters]
  //         : currentFormKey.includes("district")
  //           ? [...districtList]
  //           : item.suggestionList,
  //     };
  //   });

  //   dispatch(stockistActions.setClusterFormSectionCounter(FORM_FIELDS.length + 1));
  //   // Update the form fields state with the new form fields
  //   setFORM_FIELDS((prevFORM_FIELDS) => [...prevFORM_FIELDS, newInitialFormFields]);
  //   setDistrictsCovered([...districtsCovered, [defaultDistrict]]);
  //   dispatch(
  //     stockistActions.setFormData({
  //       value: [defaultDistrict],
  //       keyofForm: `districtsCovered__${FORM_FIELDS.length}`,
  //     })
  //   );
  // };

  // Add state for template form  //M10-11464
  const [templateFormData, setTemplateFormData] = useState<any>({
    margin: null,
    paymentTermHdr: null,
    distributionChannel: '',
    districtsCovered: [defaultDistrict],
  });
  const [selectedClustersForDuplicate, setSelectedClustersForDuplicate] = useState<any[]>([]);


  // Add state for template dropdowns
  const [templateMarginList, setTemplateMarginList] = useState<any[]>([]);
  const [templatePaymentTermList, setTemplatePaymentTermList] = useState<any[]>([]);

  const handleMultipleDuplicateCluster = async () => {
    if (selectedClustersForDuplicate.length === 0) {
      toast({
        description: "Please select at least one cluster",
        variant: "destructive",
      });
      return;
    }

    //  Use template form data instead of first section
    const templateData = {
      margin: templateFormData.margin,
      paymentTermHdr: templateFormData.paymentTermHdr,
      distributionChannel: templateFormData.distributionChannel,
      districtsCovered: templateFormData.districtsCovered,
    };

    //  Validate that template has necessary data
    if (!templateData.margin || !templateData.paymentTermHdr) {
      toast({
        description: "Please fill Margin and Payment Term in the template form",
        variant: "destructive",
      });
      return;
    }

    let newSections: any[] = [];
    const count = selectedClustersForDuplicate.length;

    for (let i = 0; i < count; i++) {
      const index = FORM_FIELDS.length + i;
      const selectedCluster = selectedClustersForDuplicate[i];

      //  Set the selected cluster
      dispatch(
        stockistActions.setFormData({
          value: selectedCluster,
          keyofForm: `clusterCode__${index}`,
        })
      );

      // Fetch margin list for this cluster
      const marginList = await getpayTerms(selectedCluster?.cluster_code, null);
      updateFormFields(marginList, "margin", index);

      //  Check if template margin exists in the API margin list
      const matchedMargin = marginList?.find(
        (item: any) => item?.margin == templateData.margin?.margin
      );

      dispatch(
        stockistActions.setFormData({
          value: matchedMargin || null,
          keyofForm: `margin__${index}`,
        })
      );

      //  Fetch payment term list
      const paymentTermCodeList = await getpayTerms(
        selectedCluster?.cluster_code,
        templateData.margin?.margin
      );
      updateFormFields(paymentTermCodeList, "paymentTermHdr", index);

      // Check if template payment term exists
      const matchedPaymentTerm = paymentTermCodeList?.find(
        (item: any) => item?.paymentTermCode === templateData.paymentTermHdr?.paymentTermCode
      );

      dispatch(
        stockistActions.setFormData({
          value: matchedPaymentTerm || null,
          keyofForm: `paymentTermHdr__${index}`,
        })
      );

      //  Copy distribution channel
      dispatch(
        stockistActions.setFormData({
          value: templateData.distributionChannel,
          keyofForm: `distributionChannel__${index}`,
        })
      );

      //  Copy districts covered
      setDistrictsCovered((prev) => [...prev, templateData.districtsCovered]);
      dispatch(
        stockistActions.setFormData({
          value: templateData.districtsCovered,
          keyofForm: `districtsCovered__${index}`,
        })
      );

      // Create new form fields
      const newInitialFormFields = initialformFields.map((item) => {
        const currentFormKey = item.keyofForm;
        const slicedFormKey = currentFormKey.split("__")[0];
        let suggestionList;
        if (currentFormKey.includes("cluster")) {
          suggestionList = clusterList;
        } else if (currentFormKey.includes("margin")) {
          suggestionList = marginList; // ✅ Use fetched margin list
        } else if (currentFormKey.includes("paymentTermHdr")) {
          suggestionList = paymentTermCodeList; // ✅ Use fetched payment term list
        } else if (currentFormKey.includes("district")) {
          suggestionList = districtList;
        }

        return {
          ...item,
          keyofForm: `${slicedFormKey}__${index}`,
          suggestionList: suggestionList,
        };
      });

      newSections.push(newInitialFormFields);
    }

    //  Update form sections counter
    dispatch(stockistActions.setClusterFormSectionCounter(FORM_FIELDS.length + count));

    //  Add all new sections
    setFORM_FIELDS((prev) => [...prev, ...newSections]);

    //  Clear selection and hide form
    setSelectedClustersForDuplicate([]);

    // Reset template form
    setTemplateFormData({
      margin: null,
      paymentTermHdr: null,
      distributionChannel: '',
      districtsCovered: [defaultDistrict],
    });

    toast({
      description: `Successfully added ${count} cluster${count > 1 ? 's' : ''} with same configuration`,
      variant: "default",
    });
  };

  // Handler for template form cluster selection (for fetching margin)
  const handleTemplateClusterSelect = async (cluster: any) => {
    if (!cluster?.cluster_code) return;

    const marginList = await getpayTerms(cluster.cluster_code, null);
    setTemplateMarginList(marginList);

    // Reset dependent fields
    setTemplateFormData((prev: any) => ({
      ...prev,
      margin: null,
      paymentTermHdr: null,
    }));
    setTemplatePaymentTermList([]);
  };

  // Handler for template margin selection
  const handleTemplateMarginSelect = async (margin: any, clusterCode: number) => {
    if (margin?.margin === null || margin?.margin === undefined || !clusterCode) return;  //M10-12174

    const paymentTermList = await getpayTerms(clusterCode, margin?.margin); //M10-12174
    setTemplatePaymentTermList(paymentTermList);

    setTemplateFormData((prev: any) => ({
      ...prev,
      margin: margin,
      paymentTermHdr: null,
    }));
  };
   //M10-11464
  // Define the function to update the form fields
  const updateFormFields = useCallback((data: any[], keyOfForm: string, index?: number | undefined) => {
    setFORM_FIELDS((prevFormFields: any) => {
      return prevFormFields.map((formFieldArray: any, arrayIndex: number) => {
        if (index === undefined || arrayIndex === index) {
          return formFieldArray.map((formField: any) => {
            if (formField.keyofForm.split("_")[0] === keyOfForm) {
              return {
                ...formField,
                suggestionList: [...data],
              };
            }
            return formField;
          });
        }
        return formFieldArray;
      });
    });
  }, []);

  const handleRemoveDivision = (indexToRemove: number) => {
    // Get the current form data from the state
    const currentFormData: any = structuredClone(addEditFormData);

    const uniqueSectionsSet = new Set();

    const sortedKeys = Object.keys(currentFormData).sort((a, b) => {
      const indexA = parseInt(a.split("__")[1]);
      const indexB = parseInt(b.split("__")[1]);
      return indexA - indexB;
    });

    // Iterate over the keys in the form data
    sortedKeys.forEach((key) => {
      // Check if the key includes "__"
      if (key.includes("__")) {
        // Split the key into the field name and index
        const [fieldName, index] = key.split("__");

        // Parse the index into a number
        const parsedIndex = parseInt(index);

        // Check if the index is greater than the index to remove
        if (parsedIndex > indexToRemove) {
          // Decrease the index by 1
          const newIndex = parsedIndex - 1;
          // Create the new key
          const newKey = `${fieldName}__${newIndex}`;

          // Update the key in the form data
          currentFormData[newKey] = currentFormData[key];
          delete currentFormData[key];
        }
        if (parsedIndex === indexToRemove) {
          delete currentFormData[key];
        }

        if (parsedIndex !== indexToRemove) uniqueSectionsSet.add(parsedIndex);
      }
    });

    // Create a copy of the form fields and remove the specified index
    let updatedFormFields = [...FORM_FIELDS];
    updatedFormFields.splice(indexToRemove, 1);

    // Adjust the keys in the form fields
    updatedFormFields = updatedFormFields.map((formField, i) => {
      return formField.map((field) => {
        const slicedFormKey = field.keyofForm?.split("__")[0];
        const newFormKey = slicedFormKey + "__" + i;
        const updatedField = { ...field, keyofForm: newFormKey };
        return updatedField;
      });
    });

    setFORM_FIELDS(updatedFormFields);
    setDistrictsCovered(districtsCovered.filter((_, i) => i !== indexToRemove));

    dispatch(stockistActions.setClusterFormSectionCounter(uniqueSectionsSet.size || 1));

    // Update the form data in the redux store
    dispatch(stockistActions.setStockistLinkFormFullObject(currentFormData));
  };

  // remove duplicate cluster from suggestion list

  useEffect(() => {
    let updatedClusters: any = structuredClone(originalClusterList);

    Object.keys(addEditFormData)?.forEach((key: any) => {
      // If the key starts with "divisionAutoComplete", filter the divisionList
      if (key.toString().startsWith("clusterCode")) {
        updatedClusters = updatedClusters.filter((cluster: any) => cluster.cluster_code !== addEditFormData[key]?.cluster_code);
        updateFormFields(updatedClusters, "clusterCode");
      }
    });

    dispatch(counterActions.setClusterList(updatedClusters));
  }, [addEditFormData]);

  const autocompleteHandler = async (data: any, keyofForm: string, index: number) => {
    switch (keyofForm.split("_")[0]) {
      case "clusterCode":
        dispatch(
          stockistActions.setFormData({
            keyofForm: `margin__${index}`,
            value: null,
          })
        );
        dispatch(
          stockistActions.setFormData({
            keyofForm: `paymentTermHdr__${index}`,
            value: null,
          })
        );
        const marginList = await getpayTerms(data?.cluster_code, null);
        updateFormFields(marginList, "margin", index);
        break;

      case "margin":
        dispatch(
          stockistActions.setFormData({
            keyofForm: `paymentTermHdr__${index}`,
            value: null,
          })
        );
        const selectedCluster = addEditFormData[`clusterCode__${index}`];
        const paymentTermCodeList = await getpayTerms(selectedCluster?.cluster_code, data?.margin);
        updateFormFields(paymentTermCodeList, "paymentTermHdr", index);
        break;
    }
  };

  const handleDistrictChange = (index: number, newDistricts: any) => {
    const updatedDistricts = [...districtsCovered];
    updatedDistricts[index] = newDistricts;
    setDistrictsCovered(updatedDistricts);
  };

  // const checkIfHeadquarterExists = async (data: any, isEdit: boolean) => {
  //   // try {
  //   //   setHeaderQuarterError("");
  //   //   if (!isEdit && data) {
  //   //     const res = await checkHeadquarterStatus(data, addEditFormData?.name?.code);
  //   //     if (res?.status !== 200) throw new Error();
  //   //     if (res?.data?.status === true) {
  //   //       // headquarter already exists
  //   //       setHeaderQuarterError("Headquarter already exists");
  //   //     }
  //   //   }
  //   // } catch (error) {
  //   //   setHeaderQuarterError("");
  //   // }
  // };

  // Render the SmartForm component
  // M10-11464
  const filteredClusterList = clusterList.filter((cluster: any) => {
    return !Object.keys(addEditFormData).some(
      (key) =>
        key.startsWith("clusterCode") &&
        addEditFormData[key]?.cluster_code === cluster.cluster_code
    );
  });
  // M10-11464
  return (
    <aside className="relative grid grid-cols-1 py-6 md:grid-cols-2 gap-y-4 gap-x-10">
      <div
        className={`justify-between flex-row-reverse md:flex-row md:absolute flex flex-wrap items-center top-4 ${FORM_FIELDS?.length > 1 ? "left-36" : "left-64"
          }`}
      >
        {FORM_FIELDS?.length > 1 && (
          <Button onClick={() => handleRemoveDivision(0)} className="text-xs font-medium text-red-500 ">
            - Remove Cluster
          </Button>
        )}
         {/* M10-11464 */}
        {/* <Button className=" text-[#5E3CEC] focus:ring-0  left-64 top-4 text-xs font-medium " onClick={handleAddDivision}>
          + Add Cluster
        </Button> */}
         {/* M10-11464 */}
      </div>

      {FORM_FIELDS?.map((formfield: any[], index: number) => {
        return (
          <>
              <div className="relative w-full md:col-span-2">
                <Divider />
                <Button
                  onClick={() => handleRemoveDivision(index)}
                  className="absolute right-0 md:right-[52%] text-xs font-medium text-red-500 top-2"
                >
                  - Remove Cluster
                </Button>
              </div>
            {formfield?.map(({ label, type, id, keyofForm, suggestionList, displayKey, uniqueKey, disabled, additionalfield }: any) => {
              switch (type?.toString().toLocaleLowerCase().trim()) {
                case "autocomplete":
                  return (
                    <Fragment key={id}>
                      <div className="w-[363px]">
                        <SmartForm.Autocomplete
                          suggestionList={suggestionList || []}
                          displayKey={displayKey || ""}
                          fieldName={keyofForm}
                          placeholder={""}
                          label={label}
                          className=""
                          disabled={disabled ? true : false}
                          uniqueKey={uniqueKey || ""}
                          isControlled={true}
                          value={
                            addEditFormData?.[keyofForm]
                              ? typeof addEditFormData[keyofForm] === "object"
                                ? addEditFormData?.[keyofForm]?.[displayKey]?.toString()
                                : addEditFormData?.[keyofForm]?.toString()
                              : ""
                          }
                          onChange={(data) => {
                            dispatch(stockistActions.setFormData({ keyofForm, value: data }));
                            autocompleteHandler(data, keyofForm, index);
                          }}
                          additionalfield={additionalfield}
                        />
                      </div>
                    </Fragment>
                  );
                case "multiselect":
                  return (
                    <div key={id} className="w-[363px]">
                      <SmartForm.MultiSelect
                        fieldName={keyofForm}
                        label={label}
                        suggestionList={suggestionList || []}
                        displayKey={displayKey || ""}
                        disabled={disabled ? true : false}
                        uniqueKey={uniqueKey || ""}
                        controlledValue={districtsCovered[index]}
                        onChange={(data) => {
                          dispatch(
                            stockistActions.setFormData({
                              value: data,
                              keyofForm: keyofForm,
                            })
                          );
                          handleDistrictChange(index, data);
                          autocompleteHandler(data, keyofForm, index);
                        }}
                        placeholder={""}
                        className="w-[363px]"
                      />
                    </div>
                  );

                default:
                  return (
                    <SmartForm.Input
                      type={type}
                      fieldName={keyofForm}
                      placeholder=""
                      label={label}
                      disabled={disabled ? true : false}
                      key={id}
                      blurHandler={(data, keyofForm) => dispatch(stockistActions.setFormData({ keyofForm, value: data }))}
                      className="w-[363px] mr-auto"
                    />
                  );
              }
            })}
          </>
        );
      })}
       {/* M10-11464 */}
      <div className="md:col-span-2 mt-6 p-4 border border-gray-300 rounded-lg bg-gray-50">
        <h3 className="text-sm font-semibold mb-3 text-gray-700">
          Cluster Configuration
        </h3>
        <p className="text-xs text-gray-600 mb-3">
          Select multiple clusters and fill in the configuration template to create duplicate sections.
        </p>

        <div className="flex flex-col gap-4">
          {/* Step 1: Select Clusters */}
          <div className="w-full md:w-[400px]">
            {/* M10-11464 */}
            <div className="flex justify-between items-center mb-1">
              <label className="font-medium"></label>
              <button
                type="button"
                className="text-sm text-blue-600 hover:underline"
                onClick={() => {
                  if (selectedClustersForDuplicate.length === filteredClusterList.length) {
                    // If all are selected, unselect all
                    setSelectedClustersForDuplicate([]);
                  } else {
                    // Select all
                    setSelectedClustersForDuplicate([...filteredClusterList]);
                    handleTemplateClusterSelect(filteredClusterList[0]);
                  }
                }}
              >
                {selectedClustersForDuplicate.length === filteredClusterList.length
                  ? 'Unselect All'
                  : 'Select All'}
              </button>
            </div>
            {/* M10-11464 */}
            <SmartForm.MultiSelect
              fieldName="selectedClustersForDuplicate"
              label="Step 1: Select Clusters"
              suggestionList={filteredClusterList}
              displayKey="cluster_name"
              uniqueKey="cluster_code"
              controlledValue={selectedClustersForDuplicate}
              onChange={(data) => {
                setSelectedClustersForDuplicate(data);
                if (data.length > 0 && data[0]) {
                  handleTemplateClusterSelect(data[0]);
                }
              }}
              placeholder="Select clusters..."
              className="w-full"
            />
          </div>

          {selectedClustersForDuplicate.length > 0 && (
            <div className="text-xs text-gray-600 bg-blue-50 p-2 rounded">
              <strong>Selected:</strong>{' '}
              {selectedClustersForDuplicate.map((c: any) => c.cluster_name).join(', ')}
            </div>
          )}

          {/* Step 2: Template Form */}
          {selectedClustersForDuplicate.length > 0 && (
            <>
              <div className="border-t pt-4">
                <h4 className="text-sm font-semibold mb-3 text-gray-700">
                  Step 2: Fill Configuration Template
                </h4>
                <p className="text-xs text-gray-600 mb-3">
                  This configuration will be applied to all selected clusters
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Margin */}
                <div className="w-full">
                  <SmartForm.Autocomplete
                    suggestionList={templateMarginList}
                    displayKey="margin"
                    fieldName="template_margin"
                    placeholder="Select Margin"
                    label="Margin *"
                    uniqueKey="margin"
                    isControlled={true}
                    className=""
                    value={templateFormData.margin?.margin?.toString() || ''}
                    onChange={(data) => {
                      handleTemplateMarginSelect(data, selectedClustersForDuplicate[0]?.cluster_code);
                    }}
                  />
                </div>

                {/* Payment Term Code */}
                <div className="w-full">
                  <SmartForm.Autocomplete
                    suggestionList={templatePaymentTermList}
                    displayKey="paymentTermCode"
                    fieldName="template_paymentTermHdr"
                    placeholder="Select Payment Term"
                    label="Payment Term Code *"
                    uniqueKey="id"
                    isControlled={true}
                    className=""
                    value={templateFormData.paymentTermHdr?.paymentTermCode?.toString() || ''}
                    onChange={(data) => {
                      setTemplateFormData((prev: any) => ({
                        ...prev,
                        paymentTermHdr: data,
                      }));
                    }}
                    disabled={!templateFormData.margin}
                  />
                </div>

                {/* Distribution Channel */}
                <div className="w-full">
                  <SmartForm.Input
                    type="input"
                    fieldName="template_distributionChannel"
                    placeholder="Enter Distribution Channel"
                    label="Distribution Channel"
                    className="w-[363px] mr-auto"
                    blurHandler={(data) => {
                      setTemplateFormData((prev: any) => ({
                        ...prev,
                        distributionChannel: data,
                      }));
                    }}
                  />
                </div>

                {/* Districts To Be Covered */}
                <div className="w-full">
                  <SmartForm.MultiSelect
                    fieldName="template_districtsCovered"
                    label="Districts To Be Covered"
                    suggestionList={districtList}
                    displayKey="district_name"
                    uniqueKey="district_code"
                    controlledValue={templateFormData.districtsCovered}
                    onChange={(data) => {
                      setTemplateFormData((prev: any) => ({
                        ...prev,
                        districtsCovered: data,
                      }));
                    }}
                    placeholder="Select districts..."
                    className="w-[363px]"
                  />
                </div>
              </div>
            </>
          )}

          {/* Step 3: Action Buttons */}
          {selectedClustersForDuplicate.length > 0 && (
            <div className="flex gap-2 border-t pt-4">
              <Button
                className="text-[#5E3CEC] bg-white border border-[#5E3CEC] hover:bg-purple-50 text-xs font-medium px-4 py-2"
                onClick={handleMultipleDuplicateCluster}
                disabled={!templateFormData.margin || !templateFormData.paymentTermHdr}
              >
                + Create {selectedClustersForDuplicate.length} Cluster{selectedClustersForDuplicate.length !== 1 ? 's' : ''} with Template
              </Button>

              <Button
                className="text-red-500 bg-white border border-red-500 hover:bg-red-50 text-xs font-medium px-4 py-2"
                onClick={() => {
                  setSelectedClustersForDuplicate([]);
                  setTemplateFormData({
                    margin: null,
                    paymentTermHdr: null,
                    distributionChannel: '',
                    districtsCovered: [defaultDistrict],
                  });
                  setTemplateMarginList([]);
                  setTemplatePaymentTermList([]);
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
       {/* M10-11464 */}
    </aside>
  );
};

// Define the Divider component
const Divider = () => <div className="h-[0.0625rem] w-[95%] bg-[#00000033] mx-auto" />;

// Export the StockistCluster component
export default StockistCluster;
